import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = [
  { v: "accredited_investor", t: "Accredited Investor", d: "Meets income / net-worth thresholds" },
  { v: "qualified_purchaser", t: "Qualified Purchaser", d: "Holds qualifying investments" },
  { v: "professional_client", t: "Professional Client", d: "Institutional / professional standing" },
];

export default function Accreditation() {
  const navigate = useNavigate();
  const { setMember } = useAuth();
  const [category, setCategory] = useState("");
  const [declared, setDeclared] = useState(false);
  const [consent, setConsent] = useState({ data_transfer: false, partner_handoff: false, final_acknowledgment: false });
  const [signature, setSignature] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const setC = (k) => setConsent((c) => ({ ...c, [k]: !c[k] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!category) return toast.error("Select your investor category.");
    if (!declared) return toast.error("You must confirm your self-declaration.");
    if (!consent.data_transfer || !consent.partner_handoff || !consent.final_acknowledgment)
      return toast.error("All three consent items are required.");
    if (!signature.trim()) return toast.error("Type your full name as signature.");

    setSubmitting(true);
    try {
      const res = await api.post("/accreditation", {
        category,
        declared,
        consent: { ...consent, typed_signature: signature.trim() },
      });
      setMember(res.data);
      toast.success("Accreditation & consent recorded.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <p className="font-mono-ui text-[10px] uppercase tracking-[0.3em] text-emerald-400/80 mb-3">
        Step 03 ◆ Accreditation & Consent
      </p>
      <h1 className="font-serif-display text-3xl sm:text-4xl tracking-tight text-slate-50 leading-tight">
        Declare & consent
      </h1>
      <p className="text-sm text-slate-400 mt-3 leading-relaxed">
        A guided self-declaration of your accredited / qualified investor status, followed by written data-transfer
        consent for the regulated partner handoff.
      </p>

      <form onSubmit={submit} className="mt-8 space-y-5">
        <div>
          <label className="block font-mono-ui text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-3">Investor category</label>
          <div className="space-y-3">
            {CATEGORIES.map((opt) => (
              <button
                type="button"
                key={opt.v}
                data-testid={`accreditation-category-${opt.v}`}
                onClick={() => setCategory(opt.v)}
                className={`w-full text-left rounded-lg border p-4 transition-colors ${
                  category === opt.v
                    ? "border-emerald-500/60 bg-emerald-950/30"
                    : "border-slate-800 bg-[#0D131C]/60 hover:border-slate-700"
                }`}
              >
                <p className="text-sm font-medium text-slate-100">{opt.t}</p>
                <p className="text-xs text-slate-500 mt-1">{opt.d}</p>
              </button>
            ))}
          </div>
        </div>

        <ConsentRow
          testId="accreditation-declared-checkbox"
          checked={declared}
          onToggle={() => setDeclared((v) => !v)}
          text="I self-declare that the investor category selected above accurately reflects my status, and I can provide supporting evidence upon request."
        />

        <div className="rounded-xl border border-slate-800 bg-[#0D131C]/70 p-5 space-y-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            <p className="font-mono-ui text-[10px] uppercase tracking-[0.2em] text-slate-300">Written Data-Transfer Consent</p>
          </div>
          <ConsentRow
            testId="consent-checkbox-data-transfer"
            checked={consent.data_transfer}
            onToggle={() => setC("data_transfer")}
            text="I consent to EqhoSense transferring my verification file and data to the approved regulated onboarding partner."
          />
          <ConsentRow
            testId="consent-checkbox-partner-handoff"
            checked={consent.partner_handoff}
            onToggle={() => setC("partner_handoff")}
            text="I understand the regulated partner — not EqhoSense — handles final account approval, funding and reporting directly with me."
          />
          <ConsentRow
            testId="consent-checkbox-final-acknowledgment"
            checked={consent.final_acknowledgment}
            onToggle={() => setC("final_acknowledgment")}
            text="I acknowledge EqhoSense does not receive client funds, provide investment advice, or guarantee any result."
          />
        </div>

        <div>
          <label className="block font-mono-ui text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-2">Typed signature</label>
          <input
            data-testid="typed-signature-input"
            className="hairline-input w-full rounded-lg px-4 py-3 text-sm font-serif-display text-lg"
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            placeholder="Type your full legal name"
          />
          <p className="font-mono-ui text-[9px] uppercase tracking-[0.15em] text-slate-600 mt-2">
            Binding with timestamp & client IP on submission
          </p>
        </div>

        <button
          type="submit"
          disabled={submitting}
          data-testid="accreditation-submit-button"
          className="w-full flex items-center justify-center gap-2 bg-emerald-500 text-slate-950 font-medium rounded-xl py-4 hover:bg-emerald-400 transition-colors active:scale-[0.99] disabled:opacity-50"
        >
          {submitting ? "Recording…" : "Sign & Submit Consent"}
        </button>
      </form>
    </Layout>
  );
}

const ConsentRow = ({ testId, checked, onToggle, text }) => (
  <label className="flex items-start gap-3 cursor-pointer">
    <Checkbox
      data-testid={testId}
      checked={checked}
      onCheckedChange={onToggle}
      className="mt-0.5 border-slate-600 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
    />
    <span className="text-xs text-slate-400 leading-relaxed">{text}</span>
  </label>
);
